import { useCallback, useEffect, useRef, useState } from "react";
import { listPurchaseOrders } from "../../services";
import {
  PurchaseOrderListFilter,
  PurchaseOrderListItem,
  PurchaseOrderStatusSummary,
} from "../../types/purchase-order.type";
import {
  EMPTY_PURCHASE_ORDER_STATUS_SUMMARY,
  PO_DEFAULT_LIST_LIMIT,
} from "./data";

export interface PurchaseOrderListState {
  rows: PurchaseOrderListItem[];
  /** จำนวนทั้งหมดที่ตัวกรองครอบคลุม (ไม่ใช่จำนวนในหน้า) */
  totalCount: number;
  /** จำนวนแยกตามสถานะ นับทั้งชุดโดยไม่สนตัวกรองสถานะ — ใช้กับแถบสรุปและแถบแดง */
  summary: PurchaseOrderStatusSummary;
  loading: boolean;
  page: number;
  limit: number;
  changePage: (page: number) => void;
  changeLimit: (limit: number) => void;
  /** แทนที่แถวเดียวด้วยค่าที่ API คืนมา โดยไม่อ่านรายการใหม่ทั้งหน้า */
  replaceRow: (row: PurchaseOrderListItem) => void;
  /** อ่านรายการซ้ำ · showSkeleton=false เมื่อไม่อยากให้ตารางกระพริบ */
  reload: (showSkeleton: boolean) => Promise<void>;
}

/**
 * สถานะของรายการ PO หนึ่งหน้า ใช้ร่วมกันระหว่าง W1 (HO) และ W7 (สาขา)
 *
 * `filter` ต้องเป็น object ที่ memo ไว้แล้ว เพราะการอ่านรายการผูกกับตัวมันโดยตรง —
 * object ใหม่ทุกรอบ render จะกลายเป็นการยิงคำขอไม่สิ้นสุด
 */
export function usePurchaseOrderList(
  filter: PurchaseOrderListFilter,
): PurchaseOrderListState {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(PO_DEFAULT_LIST_LIMIT);
  const [rows, setRows] = useState<PurchaseOrderListItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [summary, setSummary] = useState<PurchaseOrderStatusSummary>(
    EMPTY_PURCHASE_ORDER_STATUS_SUMMARY,
  );
  const [loading, setLoading] = useState<boolean>(false);

  // คำขอที่ช้ากว่าต้องไม่ทับคำตอบของคำขอที่ใหม่กว่า (ช่องค้นหายิงต่อเนื่องได้)
  const latestRequestRef = useRef<number>(0);

  // ตัวกรองที่แคบลงมักทำให้หน้าเดิมเลยท้ายรายการไปแล้ว · รีเซ็ตในรอบ render เดียวกัน
  // แทนที่จะรีเซ็ตใน effect เพื่อไม่ให้ยิงคำขอสองครั้ง (หน้าเดิมแล้วค่อยหน้าแรก)
  const [appliedFilter, setAppliedFilter] = useState<PurchaseOrderListFilter>(
    filter,
  );
  if (appliedFilter !== filter) {
    setAppliedFilter(filter);
    setPage(1);
  }

  const reload = useCallback(
    async (showSkeleton: boolean) => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;

      if (showSkeleton) setLoading(true);
      try {
        const {
          data: { data },
        } = await listPurchaseOrders({ ...filter, page, limit });

        if (latestRequestRef.current !== requestId) return;

        setRows(data?.rows ?? []);
        setTotalCount(data?.count ?? 0);
        setSummary(data?.summary ?? EMPTY_PURCHASE_ORDER_STATUS_SUMMARY);
      } finally {
        if (showSkeleton && latestRequestRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [filter, page, limit],
  );

  useEffect(() => {
    reload(true);
  }, [reload]);

  const changePage = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const changeLimit = useCallback((nextLimit: number) => {
    setLimit(nextLimit);
    setPage(1);
  }, []);

  const replaceRow = useCallback((updated: PurchaseOrderListItem) => {
    setRows((previous) =>
      previous.map((row) => (row.uuid === updated.uuid ? updated : row)),
    );
  }, []);

  return {
    rows,
    totalCount,
    summary,
    loading,
    page,
    limit,
    changePage,
    changeLimit,
    replaceRow,
    reload,
  };
}
